/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { LinkWrap } from '@/components/blocks/Linkable';
import { headerMegaMenuColumnSX } from '@/components/content/Header/styles/navBar/dropMenu';
import { headerMegaMenuItemSX } from '@/components/content/Header/styles/navBar/dropMenuItem';
import { useContentRecommendation } from '@/data/Content/ContentRecommendation';
import { PageLink } from '@/data/Navigation';
import { Box, MenuItem, MenuList, Stack, useTheme } from '@mui/material';
import { FC, useMemo } from 'react';

type JSXChildren = JSX.Element[] | JSX.Element;
const MAX_LEVELS = 3;

export const HeaderNavBarMenuList: FC<{
	tree?: PageLink[];
	display?: 'vertical' | 'horizontal';
	level?: number;
}> = ({ tree, display = 'horizontal', level = 1 }) => {
	const theme = useTheme();
	const Element = useMemo(
		() =>
			level < MAX_LEVELS && tree?.find(({ children }) => children.length > 0)
				? (props: { children: JSXChildren }) => (
						<Stack
							direction={display === 'horizontal' ? 'row' : 'column'}
							spacing={display === 'horizontal' ? 2 : ''}
							sx={{ display: 'flex', justifyContent: 'center', width: '100%', px: 3, gap: 2 }}
							{...props}
						/>
				  )
				: (props: { children: JSXChildren }) => <MenuList {...props} />,
		[display, tree, level]
	);
	const anyParents = level < MAX_LEVELS && !!tree?.some(({ children }) => children.length > 0);
	const { data, loading } = useContentRecommendation('MegaMenu_KakelKlinker');

	return level <= MAX_LEVELS && tree?.length ? (
		<Element>
			{tree.map(({ label, url, children }) => (
				<Box key={`${label}${url}`} sx={headerMegaMenuColumnSX(theme, level)}>
					<LinkWrap href={url}>
						<MenuItem
							component="a"
							sx={headerMegaMenuItemSX({ isParent: anyParents })}
							data-testid={`header-link-${label}`}
							id={`header-link-${label}`}
						>
							<Box dangerouslySetInnerHTML={{ __html: label }}></Box>
						</MenuItem>
					</LinkWrap>
					{level === 1 && label === 'Kakel & Klinker' ? (
						<HeaderNavBarMenuList
							tree={data?.map(
								(content) =>
									({
										label: content.text?.replace(/(<([^>]+)>)/gi, ''),
										url: content.contentUrl,
										children: [],
									} as PageLink)
							)}
							display={display}
							level={level + 1}
						/>
					) : (
						<HeaderNavBarMenuList tree={children} display={display} level={level + 1} />
					)}
				</Box>
			))}
		</Element>
	) : null;
};
